import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText,
  Calendar,
  BarChart3,
  Loader2,
  Plus,
  Settings,
} from 'lucide-react';
import LandingNavbar from '@/components/LandingNavbar';
import InflightAnalysisIndicator from '@/components/InflightAnalysisIndicator';
import DevelopmentMandateForm from '@/components/DevelopmentMandateForm';
import { Button } from '@/components/ui/button';
import {
  getProject,
  listProjectScripts,
  updateProject,
} from '@/services/projectService';
import { getWorkflow } from '@/services/workflowService';
import type { WorkflowScriptListItem } from '@/types/workflow';
import {
  formatAggregateScore,
  isWorkflowCompleted,
  workflowStatusLabel,
} from '@/config/workflowStatus';
import {
  EMPTY_DEVELOPMENT_MANDATE,
  normalizeMandate,
  type DevelopmentMandateSelections,
} from '@/config/developmentMandate';
import { getInflightAnalysis, setInflightAnalysis } from '@/utils/inflightAnalysisStorage';
import {
  getActiveProjectId,
  setActiveProjectId,
} from '@/utils/projectWorkflowStorage';
import type { ScriptProject } from '@/types/scriptProject';

const WORKFLOW_SESSION_KEY = 'workflow_active_session_id';

const formatDate = (dateValue: unknown): string => {
  if (!dateValue) return '';
  try {
    const d =
      typeof dateValue === 'object' &&
      dateValue !== null &&
      'toDate' in dateValue &&
      typeof (dateValue as { toDate: () => Date }).toDate === 'function'
        ? (dateValue as { toDate: () => Date }).toDate()
        : new Date(String(dateValue));
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

type ScriptRow = WorkflowScriptListItem & {
  overall_score?: number;
  engagement_percentage?: number;
};

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ScriptProject | null>(null);
  const [scripts, setScripts] = useState<ScriptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [mandateDraft, setMandateDraft] = useState<DevelopmentMandateSelections>({
    ...EMPTY_DEVELOPMENT_MANDATE,
  });
  const [savingMandate, setSavingMandate] = useState(false);
  const [mandateSaveNotice, setMandateSaveNotice] = useState<string | null>(null);

  const enrichScripts = useCallback(async (items: WorkflowScriptListItem[]) => {
    return Promise.all(
      items.map(async (item) => {
        const id = item.analysis_session_id || item.session_id;
        try {
          const wf = await getWorkflow(id);
          return {
            ...item,
            steps: wf.steps,
            script_title: wf.script_title ?? item.script_title,
            gcs_path: wf.gcs_path ?? item.gcs_path,
          } as ScriptRow;
        } catch {
          return item as ScriptRow;
        }
      }),
    );
  }, []);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      setActiveProjectId(projectId);
      const proj = await getProject(projectId);
      setProject(proj);
      setMandateDraft(normalizeMandate(proj.development_mandate));
      const items = await listProjectScripts(projectId);
      const enriched = await enrichScripts(items);
      setScripts(enriched);
      if (!getInflightAnalysis()) {
        const running = enriched.find(
          (p) => p.steps.analyse === 'in_progress' && p.analysis_session_id,
        );
        if (running?.analysis_session_id) {
          setInflightAnalysis({
            analysisSessionId: running.analysis_session_id,
            scriptTitle: running.script_title || 'Script',
            workflowSessionId: running.upload_session_id || running.session_id,
          });
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, enrichScripts]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const { inProgress, completed } = useMemo(() => {
    const inflight: ScriptRow[] = [];
    const done: ScriptRow[] = [];
    for (const s of scripts) {
      if (isWorkflowCompleted(s.steps)) done.push(s);
      else inflight.push(s);
    }
    return { inProgress: inflight, completed: done };
  }, [scripts]);

  const openWorkflow = (item: ScriptRow) => {
    const sessionId = item.analysis_session_id || item.session_id;
    if (projectId) setActiveProjectId(projectId);
    sessionStorage.setItem(WORKFLOW_SESSION_KEY, sessionId);
    navigate(`/workflow?projectId=${projectId}`);
  };

  const startNewScript = () => {
    if (!projectId) return;
    setActiveProjectId(projectId);
    sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
    navigate(`/workflow?projectId=${projectId}`);
  };

  const saveMandate = async () => {
    if (!projectId) return;
    setSavingMandate(true);
    try {
      const { project: updated, reassessed_scripts } = await updateProject(projectId, {
        development_mandate: mandateDraft,
      });
      setProject(updated);
      setShowSettings(false);
      const items = await listProjectScripts(projectId);
      const enriched = await enrichScripts(items);
      setScripts(enriched);
      const notice =
        reassessed_scripts > 0
          ? `Mandate updated — ${reassessed_scripts} script${reassessed_scripts === 1 ? '' : 's'} reassessed.`
          : 'Mandate updated.';
      setMandateSaveNotice(notice);
      window.setTimeout(() => setMandateSaveNotice(null), 5000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save mandate');
    } finally {
      setSavingMandate(false);
    }
  };

  const renderScriptCard = (item: ScriptRow) => {
    const score = formatAggregateScore(item.overall_score);
    const status = workflowStatusLabel(item.steps);

    return (
      <button
        key={item.session_id}
        type="button"
        onClick={() => openWorkflow(item)}
        className="w-full text-left p-4 rounded-lg border border-[#2a2a2a] bg-[#0d0d0d] hover:border-gold-button/40 hover:bg-[#141414] transition-colors font-sans"
      >
        <div className="flex items-start gap-3 min-w-0">
          <FileText className="h-5 w-5 text-gold-button shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-white truncate">
              {item.script_title || 'Untitled script'}
            </h3>
            <p className="text-sm text-gold-button/80 mt-1">{status}</p>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-white/45">
              {item.created_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(item.created_at)}
                </span>
              )}
              {score != null && (
                <span className="flex items-center gap-1 text-gold-button/90">
                  <BarChart3 className="h-3.5 w-3.5" />
                  Score {score}/10
                </span>
              )}
              {score == null && item.steps.analyse !== 'complete' && (
                <span className="text-white/35">Score pending</span>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  };

  if (!projectId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      <LandingNavbar variant="dark" />
      <main className="container mx-auto max-w-4xl px-4 pt-24 pb-16">
        <Link
          to="/home"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white font-sans mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          All projects
        </Link>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gold-button" />
                  </div>
                )}

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300 text-sm font-sans">
            {error}
          </div>
        )}

        {mandateSaveNotice && (
          <div className="mb-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-emerald-300 text-sm font-sans">
            {mandateSaveNotice}
                  </div>
                )}
                
        {!loading && project && (
          <>
            <header className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-sans font-bold text-white">
                  {project.name}
                </h1>
                <p className="mt-2 text-white/55 text-sm font-serif italic">
                  Scripts analysed under this project&apos;s development mandate.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setShowSettings((v) => !v)}
                  className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a] font-sans"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Project mandate
                </Button>
                <Button
                  onClick={startNewScript}
                  className="bg-gold-button text-black hover:bg-gold-light font-sans font-semibold uppercase tracking-wider rounded-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Analyse new script
                </Button>
              </div>
            </header>

            {showSettings && (
              <section className="mb-10 rounded-lg border border-[#2a2a2a] bg-[#141414] p-6 space-y-4">
                <p className="text-sm text-white/55 font-sans">
                  Project mandate overrides account defaults from Preferences for any section you
                  select here.
                </p>
                <DevelopmentMandateForm value={mandateDraft} onChange={setMandateDraft} compact />
                <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                      setMandateDraft(normalizeMandate(project.development_mandate));
                      setShowSettings(false);
                        }}
                    className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a] font-sans"
                      >
                        Cancel
                      </Button>
                        <Button
                    onClick={() => void saveMandate()}
                    disabled={savingMandate}
                    className="bg-gold-button text-black hover:bg-gold-light font-sans font-semibold"
                  >
                    {savingMandate ? 'Saving…' : 'Save mandate'}
                  </Button>
                </div>
              </section>
            )}

            {scripts.length === 0 && (
              <div className="py-16 text-center border border-[#2a2a2a] rounded-lg">
                <FileText className="h-14 w-14 mx-auto mb-4 text-white/20" />
                <p className="text-white/60 font-sans mb-4">No scripts in this project yet.</p>
                <Button 
                  onClick={startNewScript}
                  className="bg-gold-button text-black hover:bg-gold-light font-sans"
                >
                  Analyse your first script
                </Button>
              </div>
            )}

            {inProgress.length > 0 && (
              <section className="mb-10">
                <h2 className="text-xs font-sans font-semibold uppercase tracking-wider text-white/40 mb-3">
                  In progress ({inProgress.length})
                </h2>
                <div className="space-y-2">{inProgress.map(renderScriptCard)}</div>
              </section>
            )}

            {completed.length > 0 && (
              <section>
                <h2 className="text-xs font-sans font-semibold uppercase tracking-wider text-white/40 mb-3">
                  Completed ({completed.length})
                </h2>
                <div className="space-y-2">{completed.map(renderScriptCard)}</div>
              </section>
            )}
          </>
        )}
      </main>
      {getActiveProjectId() === projectId && (
        <InflightAnalysisIndicator onFinished={() => void loadData()} />
      )}
    </div>
  );
};

export default ProjectDetail; 
